import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useContext } from 'react';
import { useFinanceInfo } from '~/contexts/FinanceAndTradeContext';
import { useUserDeliveryInfo } from '~/contexts/InventoryContext';
import {
  useKeywordSuggestion,
  useSearchSource
} from '~/contexts/SearchEventsDataContext';
import UserContext from '~/contexts/UserContext';
import type { Vehicle } from '~/contracts';
import useGetSortByChanged from '~/hooks/useGetSortByChanged';
import usePriceDrop, { getPriceDropValue } from '~/hooks/usePriceDrop';
import { useDeliveryOrPickupChipGetItBy, useShippingCost } from './hooks';
import { maybeHasGreatDealTag } from './ResultTile.helpers';
import useDataForAnalytics from './useDataForAnalytics';

// ! When adding values, ask if they need to be added for the mega event as well.
export default function useSendTileInfoEvent() {
  const { trackEvent } = useContext(AnalyticsContext);
  const searchSource = useSearchSource();
  const {
    appliedFilters,
    currentPage,
    isDefaultFilters,
    searchRequestId,
    sortBy,
    totalMatchedInventory,
    tradeInfo,
    vehicles
  } = useDataForAnalytics();
  const keywordSuggestion = useKeywordSuggestion();
  const financeInfo = useFinanceInfo();
  const shippingCost = useShippingCost();
  const { restrictedState: isPriceDropRestrictedState } = usePriceDrop();
  const { isDeliveryAllowed } = useUserDeliveryInfo() ?? {};

  const { activityType, dateAvailable, getItBy, routeId } =
    useDeliveryOrPickupChipGetItBy();

  const {
    partnerId,
    userLocation: { zip }
  } = useContext(UserContext);
  const isInitialSortByRef = useGetSortByChanged();

  return useCallback(
    (vehicle: Vehicle | undefined) => {
      const { locationId, price, previousPrice, vehicleId, vehicleTags } =
        vehicle ?? {};
      const {
        apr,
        discounts,
        downPaymentDetails,
        feesMinusShipping,
        monthlyPayment,
        pricingErrorMessage,
        term,
        total,
        totalTaxes
      } = price ?? {};

      const downPayment = downPaymentDetails?.totalCashDue;
      const hasTerms = downPayment != null && monthlyPayment != null;

      const tileIndex = vehicles.findIndex(
        (v: Vehicle) => v.vehicleId === vehicleId
      );

      const eventData = {
        appliedFilters,
        currentPage,
        financeInfo,
        isDeliveryAllowed,
        isGreatDeal: maybeHasGreatDealTag(vehicle ?? {}),
        isOpenSearch: isDefaultFilters && isInitialSortByRef,
        isPriceDrop: getPriceDropValue(vehicle ?? {}) > 0,
        isPriceDropRestrictedState,
        keywordSuggestion,
        locationId,
        partnerId,
        searchRequestId,
        searchSource,
        sortBy,
        tileIndex,
        tileInfo: {
          activityType,
          apr,
          discounts,
          downPayment: hasTerms ? Number(downPayment) : downPayment,
          feesMinusShipping,
          getItBy,
          getItByDateIso: dateAvailable,
          hasTerms,
          monthlyPayment,
          pricingErrorMessage,
          previousPrice,
          routeId,
          shippingCost,
          strickerPrice: total,
          totalTaxes,
          term
        },
        totalMatchedInventory,
        tradesInfo: {
          applyTradeIn: tradeInfo?.applyTradeIn,
          totalTradeInEquity: tradeInfo?.totalTradeInEquity,
          totalTradeInValue: tradeInfo?.totalTradeInValue
        },
        vehicleId,
        vehicleTags,
        zip
      };

      trackEvent('clicked-tile-finance-terms-info', eventData);

      datadogRum.addAction('clicked-tile-finance-terms-info', eventData);
    },
    [
      activityType,
      appliedFilters,
      currentPage,
      dateAvailable,
      financeInfo,
      getItBy,
      isDefaultFilters,
      isDeliveryAllowed,
      isInitialSortByRef,
      isPriceDropRestrictedState,
      keywordSuggestion,
      partnerId,
      searchRequestId,
      searchSource,
      sortBy,
      routeId,
      shippingCost,
      totalMatchedInventory,
      trackEvent,
      tradeInfo?.applyTradeIn,
      tradeInfo?.totalTradeInEquity,
      tradeInfo?.totalTradeInValue,
      vehicles,
      zip
    ]
  );
}
